import React from 'react';
import { Button, Table } from 'reactstrap';
import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faTrash,
    faTriangleExclamation,
    faTruckRampBox,
} from '@fortawesome/free-solid-svg-icons';
import { MomentDateFormat } from '../MomentDateFormat';
import { util } from '../Util';
import { ApiRoutes } from '../ApiRoutes';
import { LinearProgress } from '@mui/material';
import JobResourceOverrideSpecifics from './JobResourceOverrideSpecifics';

export default class UpdatingEquipmentTypeTable extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            lowboyResourceEquipmentTypeIdsToRemove: [],
            lowboyResourceEquipmentTypeIdsToAdd: [],
            saving: false,
        };
    }

    stageLowboyRemoval = (dataContainer) => {
        if (!dataContainer) {
            return;
        }

        const { equipmentTypeId } = dataContainer;

        let { lowboyResourceEquipmentTypeIdsToRemove } = this.state;

        const { signalChange } = this.props;

        if (!lowboyResourceEquipmentTypeIdsToRemove.includes(equipmentTypeId)) {
            lowboyResourceEquipmentTypeIdsToRemove.push(equipmentTypeId);
        } else {
            lowboyResourceEquipmentTypeIdsToRemove =
                lowboyResourceEquipmentTypeIdsToRemove.filter(
                    (lri) => lri != equipmentTypeId
                );
        }

        this.setState({ lowboyResourceEquipmentTypeIdsToRemove });

        signalChange && signalChange();
    };

    stageLowboyAddition = (dataContainer) => {
        if (!dataContainer) {
            return;
        }

        const { equipmentTypeId } = dataContainer;

        let { lowboyResourceEquipmentTypeIdsToAdd } = this.state;

        const { signalChange } = this.props;

        if (!lowboyResourceEquipmentTypeIdsToAdd.includes(equipmentTypeId)) {
            lowboyResourceEquipmentTypeIdsToAdd.push(equipmentTypeId);
        } else {
            lowboyResourceEquipmentTypeIdsToAdd =
                lowboyResourceEquipmentTypeIdsToAdd.filter(
                    (lri) => lri != equipmentTypeId
                );
        }

        this.setState({ lowboyResourceEquipmentTypeIdsToAdd });

        signalChange && signalChange();
    };

    getRemoveLowboyResourceText = () => {
        const { equipmentTypes, dayOfDate } = this.props;

        const { lowboyResourceEquipmentTypeIdsToRemove } = this.state;

        if (!lowboyResourceEquipmentTypeIdsToRemove.length) {
            return '';
        }

        const equipmentTypeDescriptions = equipmentTypes
            .filter((et) =>
                lowboyResourceEquipmentTypeIdsToRemove.includes(et.id)
            )
            .map((et) => et.description);

        const typeOrTypes =
            lowboyResourceEquipmentTypeIdsToRemove.length > 1
                ? 'types'
                : 'type';

        return `Remove the ${moment(dayOfDate).format(
            MomentDateFormat.WeekDayNameAndDate
        )} lowboy move resourcing for the following equipment ${typeOrTypes}: ${equipmentTypeDescriptions.join(
            ', '
        )}.`;
    };

    getAddLowboyResourceText = () => {
        const { equipmentTypes, dayOfDate } = this.props;

        const { lowboyResourceEquipmentTypeIdsToAdd } = this.state;

        if (!lowboyResourceEquipmentTypeIdsToAdd.length) {
            return '';
        }

        const equipmentTypeDescriptions = equipmentTypes
            .filter((et) => lowboyResourceEquipmentTypeIdsToAdd.includes(et.id))
            .map((et) => et.description);

        const typeOrTypes =
            lowboyResourceEquipmentTypeIdsToAdd.length > 1 ? 'types' : 'type';

        return `Add lowboy move resourcing for ${moment(dayOfDate).format(
            MomentDateFormat.WeekDayNameAndDate
        )} for the following equipment ${typeOrTypes}: ${equipmentTypeDescriptions.join(
            ', '
        )}.`;
    };

    getResourceChangeText = () => {
        const removeText = this.getRemoveLowboyResourceText();
        const addText = this.getAddLowboyResourceText();

        if (removeText && addText) {
            return (
                <>
                    <div className="float-left">
                        <FontAwesomeIcon
                            icon={faTriangleExclamation}
                            className="mr-2"
                        />
                    </div>
                    <div>
                        Clicking here will:{' '}
                        <ul>
                            <li>{removeText}</li>
                            <li>{addText}</li>
                        </ul>
                    </div>
                </>
            );
        }

        return (
            <>
                <div className="float-left">
                    <FontAwesomeIcon
                        icon={faTriangleExclamation}
                        className="mr-2"
                    />
                </div>
                <div>
                    Clicking here will {removeText}
                    {addText}
                </div>
            </>
        );
    };

    getJobResourceValue = (jobResource) => {
        const { dayName } = this.props;

        if (!jobResource || !dayName) {
            return null;
        }

        return jobResource[dayName.toLowerCase()];
    };

    onSubmit = async () => {
        if (this.state.saving) {
            return;
        }

        this.setState({ saving: true });

        const { onResourcesUpdated, job, dayOfDate } = this.props;
        const {
            lowboyResourceEquipmentTypeIdsToAdd,
            lowboyResourceEquipmentTypeIdsToRemove,
        } = this.state;

        try {
            // bugbug: save goes here
            const response = await util.fetch.andGetResponse(
                util.fetch.types.post,
                ApiRoutes.lowboyResource.update(),
                {
                    jobId: job.id,
                    date: dayOfDate,
                    lowboyResourceEquipmentTypeIdsToAdd,
                    lowboyResourceEquipmentTypeIdsToRemove,
                }
            );

            if (response) {
                this.setState(
                    {
                        lowboyResourceEquipmentTypeIdsToAdd: [],
                        lowboyResourceEquipmentTypeIdsToRemove: [],
                    },
                    () => onResourcesUpdated()
                );
            }
        } finally {
            this.setState({ saving: false });
        }
    };

    componentDidUpdate(prevProps) {
        const { hideInlineSave, saving } = this.props;

        if (hideInlineSave && prevProps.saving && !saving) {
            this.onSubmit();
        }
    }

    render() {
        const {
            filteredJobResources,
            equipmentTypes,
            lowboyResources,
            equipmentTypeIdsRequiringMoves,
            dayOfDate,
            saving,
            hideInlineSave,
        } = this.props;

        const {
            lowboyResourceEquipmentTypeIdsToAdd,
            lowboyResourceEquipmentTypeIdsToRemove,
        } = this.state;

        if (saving && !hideInlineSave) {
            return <LinearProgress variant="indeterminate" color="secondary" />;
        }

        return (
            <Table size="sm" className="updating-equipment-type-table">
                <thead>
                    <tr>
                        <th>Equipment Type</th>
                        <th># of Resources</th>
                        <th>Overrides</th>
                        <th>Lowboy Move Requested</th>
                        <th>Update Lowboy Move Request</th>
                    </tr>
                </thead>
                <tbody>
                    {filteredJobResources.map((mjr, i) => {
                        const equipmentType = equipmentTypes.find(
                            (et) => et.id == mjr.equipmentTypeId
                        );

                        if (!equipmentType) {
                            return null;
                        }

                        const lowboyMoveScheduled = lowboyResources.some(
                            (lr) =>
                                lr.equipmentTypeId == mjr.equipmentTypeId &&
                                moment(dayOfDate).isSame(lr.date, 'day')
                        );

                        const lowboyRemovalRequested =
                            lowboyResourceEquipmentTypeIdsToRemove.includes(
                                mjr.equipmentTypeId
                            );

                        const lowboyMoveAvailable =
                            equipmentTypeIdsRequiringMoves.includes(
                                mjr.equipmentTypeId
                            ) &&
                            !lowboyResources.some(
                                (lr) =>
                                    lr.equipmentTypeId == mjr.equipmentTypeId
                            );

                        const lowboyMoveRequested =
                            lowboyResourceEquipmentTypeIdsToAdd.includes(
                                mjr.equipmentTypeId
                            );

                        return (
                            <tr key={`readOnlyJobResource-${i}`}>
                                <td>
                                    {equipmentType.externalId} -{' '}
                                    {equipmentType.description}
                                </td>
                                <td>{this.getJobResourceValue(mjr)}</td>
                                <td>
                                    <JobResourceOverrideSpecifics
                                        jobResourceOverrides={
                                            mjr.jobResourceOverrides
                                        }
                                        {...this.props}
                                        resourceLabel={
                                            equipmentType.description
                                        }
                                    />
                                </td>
                                <td>
                                    {Boolean(
                                        lowboyMoveAvailable ||
                                        lowboyMoveScheduled
                                    ) && (
                                        <span className="text-uppercase badge badge-yes-no badge-secondary p-1">
                                            {lowboyMoveScheduled ? 'Yes' : 'No'}
                                        </span>
                                    )}
                                </td>
                                <td>
                                    {lowboyMoveScheduled && (
                                        <Button
                                            color={
                                                lowboyRemovalRequested
                                                    ? 'danger'
                                                    : 'warning'
                                            }
                                            type="button"
                                            onClick={this.stageLowboyRemoval.bind(
                                                this,
                                                {
                                                    equipmentTypeId:
                                                        mjr.equipmentTypeId,
                                                }
                                            )}
                                            outline={!lowboyRemovalRequested}
                                        >
                                            <FontAwesomeIcon
                                                icon={faTrash}
                                                className="mr-2"
                                            />
                                            Remove{' '}
                                            {lowboyRemovalRequested && (
                                                <>Pending</>
                                            )}
                                        </Button>
                                    )}

                                    {lowboyMoveAvailable && (
                                        <Button
                                            color={
                                                lowboyMoveRequested
                                                    ? 'success'
                                                    : 'info'
                                            }
                                            type="button"
                                            onClick={this.stageLowboyAddition.bind(
                                                this,
                                                {
                                                    equipmentTypeId:
                                                        mjr.equipmentTypeId,
                                                }
                                            )}
                                            outline={!lowboyMoveRequested}
                                        >
                                            <FontAwesomeIcon
                                                icon={faTruckRampBox}
                                                className="mr-2"
                                            />
                                            Add{' '}
                                            {lowboyMoveRequested && (
                                                <>Pending</>
                                            )}
                                        </Button>
                                    )}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>

                {Boolean(
                    !hideInlineSave &&
                    (lowboyResourceEquipmentTypeIdsToRemove.length ||
                        lowboyResourceEquipmentTypeIdsToAdd.length)
                ) && (
                    <tfoot>
                        <tr>
                            <td colSpan={4}>
                                <Button
                                    color={
                                        lowboyResourceEquipmentTypeIdsToRemove.length
                                            ? 'danger'
                                            : 'success'
                                    }
                                    type="button"
                                    className="text-left w-100"
                                    onClick={this.onSubmit}
                                >
                                    {this.getResourceChangeText()}
                                </Button>
                            </td>
                        </tr>
                    </tfoot>
                )}
            </Table>
        );
    }
}
